import { Box, Typography, Card, alpha } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PreviewIcon from '@mui/icons-material/Preview';
import TranslateIcon from '@mui/icons-material/Translate';
import Grid from '@mui/material/Grid';
import { colors } from '../../theme';

interface FeatureItemProps {
    icon: React.ReactNode;
    title: string;
    description: string;
}

const FeatureItem = ({ icon, title, description }: FeatureItemProps) => (
    <Box display="flex" alignItems="flex-start" gap={2}>
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: alpha(colors.tertiary, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.tertiary,
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <Box>
            <Typography
                sx={{
                    fontWeight: 600,
                    color: colors.slate[900],
                    fontSize: '0.875rem',
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{ color: colors.slate[500], mt: 0.25 }}
            >
                {description}
            </Typography>
        </Box>
    </Box>
);

export const EmailTemplateList = () => (
    <Box>
        {/* Page Header */}
        <Box mb={4}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                    letterSpacing: '-0.5px',
                }}
            >
                Email Templates
            </Typography>
            <Typography
                variant="body1"
                sx={{ color: colors.slate[500], mt: 0.5 }}
            >
                Manage system email templates and notifications
            </Typography>
        </Box>

        {/* Coming Soon Card */}
        <Card sx={{ overflow: 'hidden' }}>
            {/* Gradient Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${colors.tertiary}, ${colors.success})`,
                    p: 4,
                    textAlign: 'center',
                }}
            >
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        background: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <EmailIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: 'white', mb: 1 }}
                >
                    Coming Soon
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        maxWidth: 400,
                        mx: 'auto',
                    }}
                >
                    View and customize the email templates used for system
                    notifications and user communications.
                </Typography>
            </Box>

            {/* Features Preview */}
            <Box sx={{ p: 4 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: colors.slate[900],
                        mb: 3,
                    }}
                >
                    Planned Features
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<PreviewIcon sx={{ fontSize: 20 }} />}
                            title="Template Preview"
                            description="Preview how emails will appear to recipients"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<EditNoteIcon sx={{ fontSize: 20 }} />}
                            title="Content Editing"
                            description="Customize email subject lines and body content"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<TranslateIcon sx={{ fontSize: 20 }} />}
                            title="Variable Support"
                            description="Use dynamic placeholders for personalization"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FeatureItem
                            icon={<EmailIcon sx={{ fontSize: 20 }} />}
                            title="Template Types"
                            description="Welcome, password reset, session notifications, and more"
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    </Box>
);